import * as React from "react";
import { useEffect, useState } from "react";
import { DataGrid } from "@material-ui/data-grid";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, Typography } from "@material-ui/core";
import { getDiseases } from "../../../services/commonservices";
import {
  changeDiseasesAction,
  changeSelectedFamilyBackgroundsAction,
  changeSelectedDiseasesAction,
  changeSelectedPatientBackgroundsAction,
} from "../../../reducers/searchOmimState";
import { DISEASES_PAGE_SIZE, TABLE_HEIGHT } from "../../../utils/constants";
import PaginationHandlerComponent from "../PaginationHandlerComponent";
import DiseaseDetailDialog from "../diseaseDetailDialog/DiseaseDetailDialog";
import familyBackgroundIcon from "../../../images/gen/Family_background.svg";
import patientBackgroundIcon from "../../../images/gen/Patient_background.svg";

const useStyles = makeStyles((theme) => ({
  dataGridContainer: {
    height: TABLE_HEIGHT,
    overflowY: "scroll",
  },
  title: {
    color: "black",
  },
  modalTitle: {
    padding: theme.spacing(3),
    color: theme.palette.secondary.main,
    wordBreak: "break-word",
  },
  dialogContent: {},
  modalButton: {
    margin: theme.spacing(2),
    border: "1px solid #FFF",
    backgroundColor: "#F3F2FC",
    borderRadius: 30,
    cursor: "pointer",
    padding: theme.spacing(1, 3),
    color: theme.palette.text.main + "!important",
    textTransform: "capitalize",
    "&:hover": {
      border: "1px solid #FF3185",
      backgroundColor: "#FFF6FA",
    },
  },
  iconContainer: {
    display: "flex",
    gap: theme.spacing(1),
  },
  svgIcon: {
    width: 28,
    height: 28,
  },
}));

const DiseasesTableComponent = ({
  backgroundScreen = false,
  diagnosisScreen = false,
  checkBackground = false,
  hideInheritance = false,
  showScore = false,
  resetPagination,
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();

  const searchText = useSelector((state) => state.searchOmim.searchText);
  const diseases = useSelector((state) => state.searchOmim.diseases);
  const totalDiseases = useSelector((state) => state.searchOmim.totalDiseases);
  const diseasesPageSize = useSelector((state) => state.searchOmim.diseasesPageSize);
  const loading = useSelector((state) => state.common.loading);
  const selectedSymptoms = useSelector((state) => state.searchOmim.selectedSymptoms);
  const selectedDiseases = useSelector((state) => state.searchOmim.selectedDiseases);
  const selectedFamilyBackgrounds = useSelector((state) => state.searchOmim.selectedFamilyBackgrounds);
  const selectedPatientBackgrounds = useSelector((state) => state.searchOmim.selectedPatientBackgrounds);
  const symptoms = useSelector((state) => state.patient.symptoms);
  const recordId = useSelector((state) => state.patient.recordId);
  const mobileVersion = useSelector((state) => state.common.mobileVersion);
  const categoriesSelected = useSelector((state) => state.searchOmim.selectedCategories);

  const lastPage = Math.ceil(totalDiseases / diseasesPageSize);

  const [diseaseId, setDiseaseId] = useState(null);
  const [title, setTitle] = useState(null);
  const [symptoms_score, setSymptoms_score] = useState(0);
  const [page, setPage] = useState(1);
  const [columns, setColumns] = useState([]);

  const columnId = {
    field: "id",
    headerName: "ID",
    width: 83,
  };
  const columnScore = {
    field: "symptoms_score",
    headerName: intl.formatMessage({ id: "%" }),
    width: 70,
  };
  const columnTitel = {
    field: "title",
    [mobileVersion ? "width" : "flex"]: mobileVersion ? 600 : 1,
    headerName: intl.formatMessage({ id: "searchOmimComponent.diseases" }),
  };
  const columnCheck = {
    field: "is_family_background",
    headerName: " ",
    renderCell: (params) => {
      return (
        <div className={classes.iconContainer}>
          {params.row?.is_family_background && (
            <img className={classes.svgIcon} src={familyBackgroundIcon} alt={"Family Background Icon"} />
          )}
          {params.row?.is_patient_background && (
            <img className={classes.svgIcon} src={patientBackgroundIcon} alt={"Patient Background Icon"} />
          )}
        </div>
      );
    },
  };

  useEffect(() => {
    if (checkBackground) {
      if (showScore) {
        setColumns([columnId, columnScore, columnTitel, columnCheck]);
      } else  setColumns([columnId, columnTitel, columnCheck]);
    } else {
      if (showScore) {
        setColumns([columnId, columnScore, columnTitel]);
      } else {
        setColumns([columnId, columnTitel]);
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkBackground]);

  const rows = diseases.map((disease) => {
    if (checkBackground) {
      if (showScore) {
        return {
          id: disease.id,
          title: disease.title,
          symptoms_score: disease.symptoms_score,
          is_family_background: disease.is_family_background,
          is_patient_background: disease.is_patient_background,
        };
      } else {
        return {
          id: disease.id,
          title: disease.title,
          is_family_background: disease.is_family_background,
          is_patient_background: disease.is_patient_background,
        };
      }
    } else {
      if (showScore) {
        return {
          id: disease.id,
          title: disease.title,
          symptoms_score: disease.symptoms_score,
        };
      } else {
        return {
          id: disease.id,
          title: disease.title,
        };
      }
    }
  });

  const handleOpenDiseaseModal = (row) => {
    setDiseaseId(row.id);
    setTitle(diseases.find((d) => d.id === row.id)?.title || "");
    setSymptoms_score(row.row.symptoms_score);
  };

  const handleCloseDiseaseModal = () => {
    setDiseaseId(null);
    setTitle(null);
    setSymptoms_score(0);
  };

  const handleDiseasesSuccess = (response) => {
    dispatch(changeDiseasesAction(response.data));
  };

  const searchDiseases = (value) => {
    const formattedSymptoms = backgroundScreen
      ? null
      : diagnosisScreen
      ? symptoms.map((s) => s.symptom_id)
      : selectedSymptoms.map((s) => s.id);

    const newPage = !!value ? page + 1 : page - 1;

    setPage(newPage);

    const offset = diseasesPageSize * (newPage - 1);
    getDiseases(
      searchText,
      formattedSymptoms,
      checkBackground ? recordId : null,
      diseasesPageSize,
      offset,
      handleDiseasesSuccess,
    );
  };

  const isBackgroundSelected = (disease) => {
    return !!hideInheritance
      ? selectedPatientBackgrounds.find((item) => item.id === disease.id) || false
      : selectedFamilyBackgrounds.find((item) => item.id === disease.id) || false;
  };

  const handleSelectBackground = (value) => {
    const background = value.row;
    const exist = isBackgroundSelected(background);
    let newBackgrounds;

    if (!!hideInheritance) {
      if (exist) {
        newBackgrounds = selectedPatientBackgrounds.filter((item) => item !== background);
      } else {
        newBackgrounds = [...selectedPatientBackgrounds];
        newBackgrounds.push(background);
      }

      dispatch(changeSelectedPatientBackgroundsAction(newBackgrounds));
    } else {
      if (exist) {
        newBackgrounds = selectedFamilyBackgrounds.filter((item) => item !== background);
      } else {
        newBackgrounds = [...selectedFamilyBackgrounds];
        newBackgrounds.push(background);
      }

      dispatch(changeSelectedFamilyBackgroundsAction(newBackgrounds));
    }
  };

  const isDiseaseSelected = (disease) => {
    return selectedDiseases.find((item) => item.id === disease.id) || false;
  };

  const handleSelectDisease = (value) => {
    const disease = value.row;
    const exist = isDiseaseSelected(disease);
    let newDiseases;
    if (exist) {
      newDiseases = selectedDiseases.filter((item) => item !== disease);
    } else {
      newDiseases = [...selectedDiseases];
      newDiseases.push(disease);
    }

    dispatch(changeSelectedDiseasesAction(newDiseases));
  };

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesSelected, selectedSymptoms, resetPagination]);

  return (
    <>
      <Typography variant={"h2"}>{intl.formatMessage({ id: "diseases" })}</Typography>
      <div className={classes.dataGridContainer}>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          onRowClick={
            backgroundScreen ? handleSelectBackground : diagnosisScreen ? handleSelectDisease : handleOpenDiseaseModal
          }
          pageSize={DISEASES_PAGE_SIZE}
          rowsPerPageOptions={[DISEASES_PAGE_SIZE]}
          disableSelectionOnClick
          hideFooterPagination
          rowHeight={60}
          hideFooter
          loading={loading}
        />
      </div>
      {!!rows?.length && (
        <PaginationHandlerComponent
          handleClick={searchDiseases}
          disablePrevious={!!(!rows?.length || page === 1)}
          disableNext={!!(!rows?.length || (!!page && !!lastPage && page === lastPage) || !lastPage)}
          page={page}
          lastPage={lastPage}
        />
      )}
      <DiseaseDetailDialog
        title={title}
        diseaseId={diseaseId}
        handleCloseDiseaseModal={handleCloseDiseaseModal}
        symptoms_score={symptoms_score}
      />
    </>
  );
};
export default DiseasesTableComponent;
